import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

const SCROLL_RANGE = 100;

interface ScrollState {
  scrolled: boolean;
  scrollProgress: number;
}

const ScrollContext = createContext<ScrollState>({ scrolled: false, scrollProgress: 0 });

export function ScrollProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ScrollState>({ scrolled: false, scrollProgress: 0 });

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const scrollProgress = Math.min(1, y / SCROLL_RANGE);
      setState({
        scrolled: y > SCROLL_RANGE,
        scrollProgress,
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return <ScrollContext.Provider value={state}>{children}</ScrollContext.Provider>;
}

export function useScroll(): boolean {
  const context = useContext(ScrollContext);
  return context.scrolled;
}

/** 0 at top, 1 when scrolled past SCROLL_RANGE -- use for smooth logo/UI scaling */
export function useScrollProgress(): number {
  const context = useContext(ScrollContext);
  return context.scrollProgress ?? 0;
}
